import { ChevronRightIcon, HomeIcon } from '@heroicons/react/20/solid';
import Link from 'next/link';
import classNames from '@/lib/class-names';
import type { ElementType, FC, ReactNode } from 'react';

/**
 * For more details on how to use and properly
 * configure the Breadcrumb component sett {@link http://localhost:6006/?path=/docs/components-breadcrumb--docs}.
 */

const BreadcrumbRoot: FC<{ children: ReactNode; className?: string; homeHref?: string; homePosition?: number }> = ({
  children,
  className,
  homeHref = '/',
  homePosition = 1,
}) => {
  return (
    <nav aria-label='Breadcrumb' className={classNames('overflow-x-auto', className)}>
      <ol className='flex items-center space-x-4' itemScope itemType='https://schema.org/BreadcrumbList'>
        <li>
          <div>
            <Link
              className='lint text-primary hover:text-primary-focus'
              href={homeHref}
              itemProp='item'
              prefetch={false}
            >
              <HomeIcon aria-hidden='true' className='h-5 w-5 shrink-0' />
              <span className='sr-only' itemProp='name'>
                Home
              </span>
              <meta content={`${homePosition}`} itemProp='position' />
            </Link>
          </div>
        </li>

        {children}
      </ol>
    </nav>
  );
};

BreadcrumbRoot.displayName = 'Breadcrumb';

const BreadcrumbItem: FC<{
  children: ReactNode;
  className?: string;
  contentWrapperElement?: ElementType;
  href: string;
  isCurrenctPage: boolean;
  position: number;
  prefetch?: boolean;
}> = ({
  children,
  className,
  contentWrapperElement: WrapperComponent = 'span',
  href,
  isCurrenctPage,
  position,
  prefetch = false,
}) => {
  return (
    <li className={className} itemProp='itemListElement' itemScope itemType='https://schema.org/ListItem'>
      <div className='flex items-center'>
        <ChevronRightIcon aria-hidden='true' className='text-base-content h-5 w-5 shrink-0' />
        <Link
          aria-current={isCurrenctPage ? 'page' : undefined}
          className='link text-primary ml-4'
          href={href}
          itemProp='item'
          prefetch={prefetch}
        >
          <WrapperComponent itemProp='name'>{children}</WrapperComponent>
          <meta content={`${position + 1}`} itemProp='position' />
        </Link>
      </div>
    </li>
  );
};

BreadcrumbItem.displayName = 'Breadcrumb_Item';

const Breadcrumb = Object.assign(BreadcrumbRoot, { Item: BreadcrumbItem });

export default Breadcrumb;
